/**
 * DropdownMenu — wrapper sobre @radix-ui/react-dropdown-menu com tokens do DS.
 * Radix entrega focus trap, ESC, teclado, ARIA e posicionamento; reusamos.
 */

import {
  Root,
  Trigger,
  Portal,
  Content,
  Item,
  Separator,
  Label,
  Group,
  Sub,
  SubTrigger,
  SubContent,
} from "@radix-ui/react-dropdown-menu";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

export const DropdownMenu = Root;
export const DropdownMenuTrigger = Trigger;
export const DropdownMenuGroup = Group;
export const DropdownMenuSub = Sub;

export function DropdownMenuContent({
  className,
  sideOffset = 8,
  align = "end",
  ...props
}: ComponentPropsWithoutRef<typeof Content>) {
  return (
    <Portal>
      <Content
        sideOffset={sideOffset}
        align={align}
        className={cn(
          "z-50 min-w-[180px] overflow-hidden rounded-md",
          "border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg",
          "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
          "py-1",
          className,
        )}
        {...props}
      />
    </Portal>
  );
}

export function DropdownMenuItem({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Item>) {
  return (
    <Item
      className={cn(
        "relative flex cursor-pointer select-none items-center gap-2 px-3 py-2",
        "text-[13px] font-medium text-[var(--color-foreground)]",
        "outline-none transition-colors",
        "hover:bg-[var(--color-secondary)] focus:bg-[var(--color-secondary)]",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Separator>) {
  return (
    <Separator
      className={cn("my-1 h-px bg-[var(--color-border)]", className)}
      {...props}
    />
  );
}

export function DropdownMenuLabel({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof Label>) {
  return (
    <Label
      className={cn(
        "px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]",
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSubTrigger({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SubTrigger>) {
  return (
    <SubTrigger
      className={cn(
        "flex cursor-pointer select-none items-center gap-2 px-3 py-2",
        "text-[13px] font-medium text-[var(--color-foreground)]",
        "outline-none transition-colors",
        "hover:bg-[var(--color-secondary)] focus:bg-[var(--color-secondary)]",
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSubContent({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof SubContent>) {
  return (
    <Portal>
      <SubContent
        className={cn(
          "z-50 min-w-[140px] overflow-hidden rounded-md py-1",
          "border border-[var(--color-border)] bg-[var(--color-card)] shadow-lg",
          "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95",
          className,
        )}
        {...props}
      />
    </Portal>
  );
}
